import { useForwardProp } from "@faro-lotv/app-component-toolbox";
import { SmoothLine, SmoothLineMaterial } from "@faro-lotv/lotv";
import { forwardRef, useEffect, useMemo, type JSX } from "react";
import { Vector3 } from "three";

type SmoothLineRendererProps = {
  /** The list of 3D points describing the line */
  points: Vector3[];

  /** The name assigned to the underlying 3D object, mostly for debugging purposes */
  name?: string;

  /** The renderOrder assigned to the underlying 3D object */
  renderOrder?: number;
} & Pick<SmoothLineMaterial, "color" | "thickness"> &
  Partial<
    Pick<SmoothLineMaterial, "clipping" | "clippingPlanes" | "depthTest">
  >;

/**
 * @returns A smooth 3D line connecting all the input points
 */
export const SmoothLineRenderer = forwardRef<
  SmoothLine,
  SmoothLineRendererProps
>(function SmoothLineRenderer(
  {
    points,
    color,
    thickness,
    clippingPlanes,
    clipping = false,
    depthTest,
    name = "SmoothLineRenderer",
    renderOrder = 0,
  }: SmoothLineRendererProps,
  ref,
): JSX.Element | null {
  const object = useMemo(() => new SmoothLine(points), [points]);

  useForwardProp(object, "name", name);
  useForwardProp(object, "renderOrder", renderOrder);
  useForwardProp(object.material, "color", color);
  useForwardProp(object.material, "thickness", thickness);
  useForwardProp(object.material, "clippingPlanes", clippingPlanes ?? null);
  useForwardProp(object.material, "clipping", clipping);

  useEffect(() => {
    if (!depthTest) return;
    object.material.depthTest = depthTest;
    object.material.needsUpdate = true;
  }, [depthTest, object]);

  return <primitive object={object} ref={ref} />;
});
